import { useEffect, useMemo, useState } from 'react';
import { faMinus } from '@fortawesome/pro-regular-svg-icons/faMinus';
import { faPlus } from '@fortawesome/pro-regular-svg-icons/faPlus';
import { Alert, Field, Icon, Select } from '@kontentino/ui';
import Footer from 'app/modules/posts/components/shiftPosts/Footer';
import SelectTargetProfiles from 'app/modules/posts/components/shiftPosts/ShiftTargetProfilesSelect';
import {
  ShiftPostsTarget,
  STEPS,
  useShiftPosts,
} from 'app/modules/posts/hooks/useShiftPosts';
import { getEarliestPostDate } from 'app/modules/posts/utils/getEarliestPostDate';
import Modal from 'components/shared/Modal';
import dayjs, { Dayjs } from 'dayjs';
import { usePage } from 'modules/page/pageSelector';
import { useTranslation } from 'react-i18next';
import { Project } from 'types/Page';
import { ListPost } from 'types/PostDetail';
import { v4 } from 'uuid';
import MovePostPreview from './MovePostPreview';

type Props = {
  initialPosts: ListPost[];
  initialProjects?: Record<string, Project[]>;
  onFinish: () => void;
};

type PostScheduleState = ListPost & {
  date?: string | null;
  time?: string | null;
};

type SchedulingMode = 'keep-original' | 'shift-forward' | 'custom-schedule';

type ShiftOption = 'next-week' | 'next-month' | 'custom';

const SHIFT_OPTIONS = [
  { value: 'next-week' as const, label: 'Next week (+7 days)' },
  { value: 'next-month' as const, label: 'Next month (+30 days)' },
  { value: 'custom' as const, label: 'Custom' },
];

const MIN_SHIFT_DAYS = 1;
const MAX_SHIFT_DAYS = 365;
const DEFAULT_CUSTOM_DAYS = 3;

function MovePostsPreselectedPosts(props: Props) {
  const container = useShiftPosts({
    initialStep: STEPS.SELECT_TARGET_PROFILES.step,
    ...props,
  });
  const { pages } = usePage();
  const { t } = useTranslation();

  const isMultiProfile = useMemo(() => {
    const uniquePageIds = new Set(
      container.selectedPosts.map((p) => p.page.id),
    );
    return uniquePageIds.size > 1;
  }, [container.selectedPosts]);

  useEffect(() => {
    if (!isMultiProfile) return;

    const postsByPage = container.selectedPosts.reduce<
      Record<number, number[]>
    >((acc, post) => {
      const pageId = post.page.id;
      if (!acc[pageId]) acc[pageId] = [];
      acc[pageId].push(post.id);
      return acc;
    }, {});

    const lockedTargets: ShiftPostsTarget[] = Object.entries(postsByPage).map(
      ([pageId, postIds]) => ({
        id: v4(),
        profileId: Number(pageId),
        projectId: undefined,
        locked: true,
        postIds,
      }),
    );

    container.setTargets(lockedTargets);
    setSchedulingMode('shift-forward');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMultiProfile]);

  const [schedulingMode, setSchedulingMode] =
    useState<SchedulingMode>('keep-original');
  const [shiftOption, setShiftOption] = useState<ShiftOption>('next-week');
  const [customDays, setCustomDays] = useState<number>(DEFAULT_CUSTOM_DAYS);
  const [postState, setPostState] = useState<PostScheduleState[]>(
    props.initialPosts.map((post) => ({
      ...post,
      date: post.date ? dayjs(post.date).format('YYYY-MM-DD') : null,
      time: post.time || null,
    })),
  );

  const effectiveShiftDays = (() => {
    switch (shiftOption) {
      case 'next-week':
        return 7;
      case 'next-month':
        return 30;
      case 'custom':
        return customDays;
    }
  })();

  const handleIncrement = () => {
    setCustomDays((prev) => Math.min(MAX_SHIFT_DAYS, prev + 1));
  };

  const handleDecrement = () => {
    setCustomDays((prev) => Math.max(MIN_SHIFT_DAYS, prev - 1));
  };

  const earliestPostDate = getEarliestPostDate(container.selectedPosts);

  const handleUpdatePostDate = (date: Dayjs | null, postId: number) => {
    setPostState((prev) =>
      prev.map((post) =>
        post.id === postId
          ? { ...post, date: date ? date.format('YYYY-MM-DD') : null }
          : post,
      ),
    );
  };

  const handleUpdatePostTime = (time: string, postId: number) => {
    setPostState((prev) =>
      prev.map((post) =>
        post.id === postId ? { ...post, time: time || null } : post,
      ),
    );
  };

  const scheduleParams = useMemo(() => {
    if (schedulingMode === 'shift-forward') {
      return { shiftDays: effectiveShiftDays };
    }

    if (schedulingMode === 'custom-schedule') {
      return {
        postsCustomSchedule: Object.fromEntries(
          postState
            .filter((post) => post.date || post.time)
            .map((post) => [
              post.id,
              {
                customScheduleDate: post.date || undefined,
                customScheduleTime: post.time || undefined,
              },
            ]),
        ),
      };
    }

    return {};
  }, [schedulingMode, effectiveShiftDays, postState]);

  return (
    <>
      <Modal.Header title={t('movePosts')} />
      <Modal.Content className="tw-pb-px">
        {container.canShowTargetProfiles && (
          <>
            <SelectTargetProfiles
              variant="move"
              selectedPosts={container.selectedPosts}
              availablePages={pages}
              errors={container.errors}
              targets={container.targets}
              removeTarget={container.removeTarget}
              targetHandlers={container.targetHandlers}
              shiftDays={scheduleParams.shiftDays}
              postsCustomSchedule={scheduleParams.postsCustomSchedule}
            />
            {!isMultiProfile &&
              container.step === STEPS.SELECT_TARGET_PROFILES.step && (
                <Alert
                  variant="warning"
                  title={t(
                    'movePosts.accessWarning.title',
                    'User Access Notice',
                  )}
                  className="tw-mt-4"
                >
                  {t(
                    'movePosts.accessWarning.message',
                    'When moving posts to a different profile, users without access to the destination profile will no longer be able to view or edit these posts.',
                  )}
                </Alert>
              )}
            {isMultiProfile &&
              container.step === STEPS.SELECT_TARGET_PROFILES.step && (
                <>
                  <Alert variant="info" className="tw-mt-4">
                    {t('movePosts.multiProfile.info', {
                      defaultValue:
                        'When you select posts from multiple profiles, you can only shift the posting dates forward by a specified number of days. To set custom dates and times for individual posts, please select posts from a single profile.',
                    })}
                  </Alert>
                  <div className="tw-mt-4">
                    <Field.Group>
                      <div className="tw-flex tw-flex-col tw-gap-3">
                        <div>
                          <label className="tw-flex tw-cursor-pointer tw-items-center tw-gap-2">
                            <input
                              type="radio"
                              name="scheduling-mode"
                              value="shift-forward"
                              checked={schedulingMode === 'shift-forward'}
                              onChange={() =>
                                setSchedulingMode('shift-forward')
                              }
                              className="tw-h-4 tw-w-4 tw-accent-primary-100"
                              data-cy="move-posts_scheduling-mode-shift-forward"
                            />
                            <span className="tw-text-md">
                              {t('postsShift.shiftAllPostsForward', {
                                defaultValue: 'Shift all posts forward',
                              })}
                            </span>
                          </label>

                          {schedulingMode === 'shift-forward' && (
                            <div className="tw-ml-6 tw-mt-3 tw-flex tw-items-start tw-gap-6">
                              <div className="tw-flex tw-w-[45%] tw-flex-col tw-gap-2">
                                <span className="tw-text-md">
                                  {t('postsShift.shiftForwardBy', {
                                    defaultValue: 'Shift forward by',
                                  })}
                                </span>
                                <Select
                                  value={SHIFT_OPTIONS.find(
                                    (opt) => opt.value === shiftOption,
                                  )}
                                  options={SHIFT_OPTIONS}
                                  onChange={(option) => {
                                    if (option) {
                                      setShiftOption(option.value);
                                    }
                                  }}
                                  menuPortalTarget={document.body}
                                  styles={{
                                    menuPortal: (styles) => ({
                                      ...styles,
                                      zIndex: 1010,
                                    }),
                                  }}
                                  data-cy="move-posts_shift-option-select"
                                />
                              </div>
                              {shiftOption === 'custom' && (
                                <div className="tw-flex tw-flex-1 tw-flex-col tw-gap-2">
                                  <span className="tw-text-md">
                                    {t('postsShift.numberOfDays', {
                                      defaultValue: 'Number of days',
                                    })}
                                  </span>
                                  <div className="tw-flex tw-items-stretch">
                                    <input
                                      type="text"
                                      value={`${customDays} ${t(
                                        'postsShift.days',
                                        { defaultValue: 'days' },
                                      )}`}
                                      onChange={(e) => {
                                        const val = parseInt(
                                          e.target.value,
                                          10,
                                        );
                                        if (!isNaN(val)) {
                                          setCustomDays(
                                            Math.min(
                                              MAX_SHIFT_DAYS,
                                              Math.max(MIN_SHIFT_DAYS, val),
                                            ),
                                          );
                                        }
                                      }}
                                      className="focus:tw-ring-0 tw-h-10 tw-flex-1 tw-rounded-l-md tw-border tw-border-r-0 tw-border-grayscale-30 tw-px-3 tw-text-md tw-text-grayscale-180 tw-outline-none focus:tw-border-primary-100"
                                      data-cy="move-posts_custom-days-input"
                                    />
                                    <button
                                      type="button"
                                      onClick={handleIncrement}
                                      disabled={customDays >= MAX_SHIFT_DAYS}
                                      className="tw-flex tw-h-10 tw-w-10 tw-items-center tw-justify-center tw-border tw-border-r-0 tw-border-grayscale-30 tw-bg-white tw-text-grayscale-100 hover:tw-bg-grayscale-5 disabled:tw-opacity-50"
                                      data-cy="move-posts_shift-days-increment"
                                    >
                                      <Icon icon={faPlus} />
                                    </button>
                                    <button
                                      type="button"
                                      onClick={handleDecrement}
                                      disabled={customDays <= MIN_SHIFT_DAYS}
                                      className="tw-flex tw-h-10 tw-w-10 tw-items-center tw-justify-center tw-rounded-r-md tw-border tw-border-grayscale-30 tw-bg-white tw-text-grayscale-100 hover:tw-bg-grayscale-5 disabled:tw-opacity-50"
                                      data-cy="move-posts_shift-days-decrement"
                                    >
                                      <Icon icon={faMinus} />
                                    </button>
                                  </div>
                                  <span className="tw-text-sm tw-text-primary-100">
                                    {t('postsShift.startingOn', {
                                      defaultValue: 'Starting on {{date}}',
                                      date: earliestPostDate
                                        .add(customDays, 'day')
                                        .format('MMMM D, YYYY'),
                                    })}
                                  </span>
                                </div>
                              )}
                            </div>
                          )}
                        </div>

                        <div>
                          <label className="tw-flex tw-cursor-pointer tw-items-center tw-gap-2">
                            <input
                              type="radio"
                              name="scheduling-mode"
                              value="custom-schedule"
                              checked={schedulingMode === 'custom-schedule'}
                              onChange={() =>
                                setSchedulingMode('custom-schedule')
                              }
                              className="tw-h-4 tw-w-4 tw-accent-primary-100"
                              data-cy="move-posts_scheduling-mode-custom-schedule"
                            />
                            <span className="tw-text-md">
                              {t('postsShift.useCustomDateTime', {
                                defaultValue: 'Use custom date and time',
                              })}
                            </span>
                          </label>

                          {schedulingMode === 'custom-schedule' && (
                            <div className="tw-ml-6 tw-mt-3 tw-flex tw-flex-col tw-gap-3">
                              {postState.map((post) => (
                                <MovePostPreview
                                  key={post.id}
                                  post={post}
                                  onDateChange={(date) =>
                                    handleUpdatePostDate(date, post.id)
                                  }
                                  onTimeChange={(time) =>
                                    handleUpdatePostTime(time, post.id)
                                  }
                                />
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    </Field.Group>
                  </div>
                </>
              )}
            {!isMultiProfile &&
              container.selectedPosts.length > 0 &&
              container.step === STEPS.SELECT_TARGET_PROFILES.step && (
                <div className="tw-mt-4">
                  <Field.Group>
                    <div className="tw-flex tw-flex-col tw-gap-3">
                      <label className="tw-flex tw-cursor-pointer tw-items-center tw-gap-2">
                        <input
                          type="radio"
                          name="scheduling-mode"
                          value="keep-original"
                          checked={schedulingMode === 'keep-original'}
                          onChange={() => setSchedulingMode('keep-original')}
                          className="tw-h-4 tw-w-4 tw-accent-primary-100"
                          data-cy="move-posts_scheduling-mode-keep-original"
                        />
                        <span className="tw-text-md">
                          {t('postsShift.keepOriginalDates', {
                            defaultValue: 'Keep original dates',
                          })}
                        </span>
                      </label>

                      <div>
                        <label className="tw-flex tw-cursor-pointer tw-items-center tw-gap-2">
                          <input
                            type="radio"
                            name="scheduling-mode"
                            value="shift-forward"
                            checked={schedulingMode === 'shift-forward'}
                            onChange={() => setSchedulingMode('shift-forward')}
                            className="tw-h-4 tw-w-4 tw-accent-primary-100"
                            data-cy="move-posts_scheduling-mode-shift-forward"
                          />
                          <span className="tw-text-md">
                            {t('postsShift.shiftAllPostsForward', {
                              defaultValue: 'Shift all posts forward',
                            })}
                          </span>
                        </label>

                        {schedulingMode === 'shift-forward' && (
                          <div className="tw-ml-6 tw-mt-3 tw-flex tw-items-start tw-gap-6">
                            <div className="tw-flex tw-w-[45%] tw-flex-col tw-gap-2">
                              <span className="tw-text-md">
                                {t('postsShift.shiftForwardBy', {
                                  defaultValue: 'Shift forward by',
                                })}
                              </span>
                              <Select
                                value={SHIFT_OPTIONS.find(
                                  (opt) => opt.value === shiftOption,
                                )}
                                options={SHIFT_OPTIONS}
                                onChange={(option) => {
                                  if (option) {
                                    setShiftOption(option.value);
                                  }
                                }}
                                menuPortalTarget={document.body}
                                styles={{
                                  menuPortal: (styles) => ({
                                    ...styles,
                                    zIndex: 1010,
                                  }),
                                }}
                                data-cy="move-posts_shift-option-select"
                              />
                            </div>
                            {shiftOption === 'custom' && (
                              <div className="tw-flex tw-flex-1 tw-flex-col tw-gap-2">
                                <span className="tw-text-md">
                                  {t('postsShift.numberOfDays', {
                                    defaultValue: 'Number of days',
                                  })}
                                </span>
                                <div className="tw-flex tw-items-stretch">
                                  <input
                                    type="text"
                                    value={`${customDays} ${t(
                                      'postsShift.days',
                                      { defaultValue: 'days' },
                                    )}`}
                                    onChange={(e) => {
                                      const val = parseInt(e.target.value, 10);
                                      if (!isNaN(val)) {
                                        setCustomDays(
                                          Math.min(
                                            MAX_SHIFT_DAYS,
                                            Math.max(MIN_SHIFT_DAYS, val),
                                          ),
                                        );
                                      }
                                    }}
                                    className="focus:tw-ring-0 tw-h-10 tw-flex-1 tw-rounded-l-md tw-border tw-border-r-0 tw-border-grayscale-30 tw-px-3 tw-text-md tw-text-grayscale-180 tw-outline-none focus:tw-border-primary-100"
                                    data-cy="move-posts_custom-days-input"
                                  />
                                  <button
                                    type="button"
                                    onClick={handleIncrement}
                                    disabled={customDays >= MAX_SHIFT_DAYS}
                                    className="tw-flex tw-h-10 tw-w-10 tw-items-center tw-justify-center tw-border tw-border-r-0 tw-border-grayscale-30 tw-bg-white tw-text-grayscale-100 hover:tw-bg-grayscale-5 disabled:tw-opacity-50"
                                    data-cy="move-posts_shift-days-increment"
                                  >
                                    <Icon icon={faPlus} />
                                  </button>
                                  <button
                                    type="button"
                                    onClick={handleDecrement}
                                    disabled={customDays <= MIN_SHIFT_DAYS}
                                    className="tw-flex tw-h-10 tw-w-10 tw-items-center tw-justify-center tw-rounded-r-md tw-border tw-border-grayscale-30 tw-bg-white tw-text-grayscale-100 hover:tw-bg-grayscale-5 disabled:tw-opacity-50"
                                    data-cy="move-posts_shift-days-decrement"
                                  >
                                    <Icon icon={faMinus} />
                                  </button>
                                </div>
                                <span className="tw-text-sm tw-text-primary-100">
                                  {t('postsShift.startingOn', {
                                    defaultValue: 'Starting on {{date}}',
                                    date: earliestPostDate
                                      .add(customDays, 'day')
                                      .format('MMMM D, YYYY'),
                                  })}
                                </span>
                              </div>
                            )}
                          </div>
                        )}
                      </div>

                      <div>
                        <label className="tw-flex tw-cursor-pointer tw-items-center tw-gap-2">
                          <input
                            type="radio"
                            name="scheduling-mode"
                            value="custom-schedule"
                            checked={schedulingMode === 'custom-schedule'}
                            onChange={() =>
                              setSchedulingMode('custom-schedule')
                            }
                            className="tw-h-4 tw-w-4 tw-accent-primary-100"
                            data-cy="move-posts_scheduling-mode-custom-schedule"
                          />
                          <span className="tw-text-md">
                            {t('postsShift.useCustomDateTime', {
                              defaultValue: 'Use custom date and time',
                            })}
                          </span>
                        </label>

                        {schedulingMode === 'custom-schedule' && (
                          <div className="tw-ml-6 tw-mt-3 tw-flex tw-flex-col tw-gap-3">
                            {postState.map((post) => (
                              <MovePostPreview
                                key={post.id}
                                post={post}
                                onDateChange={(date) =>
                                  handleUpdatePostDate(date, post.id)
                                }
                                onTimeChange={(time) =>
                                  handleUpdatePostTime(time, post.id)
                                }
                              />
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  </Field.Group>
                </div>
              )}
          </>
        )}
      </Modal.Content>
      <Footer
        variant="move"
        activeStep={container.step}
        canGoNext={container.canGoNext}
        onGoNext={container.goNext}
        onSubmit={container.onSubmit}
      />
    </>
  );
}

export default MovePostsPreselectedPosts;
